import apiWrapper from 'common/api/api-wrapper';
import {
	IAPIErrorContent,
	IUpdateControlQualityCheckRequest,
} from 'common/api/type-def';
import { CONTENT_TYPE } from 'common/constants';
import storageWrapper from 'common/loginUtils/storage-wrapper';
import { getHeader } from 'common/utils/utilFunctions';

import {
	APIResponse,
	IAIModelDetails,
	AIRecipesDataResponse,
	AIModelsDataResponse,
	IAIRecipeDetails,
	AIDeleteResponse,
	IExistingSharedData,
	AISharedDataResponse,
	ISelectDocument,
	IResultAnalysis,
	IObligationComments,
	IObligationToPolicyComment,
	IPostAIModalResp,
	IGetAISimulationRecipes,
	IShareAISimulation,
	IGetAISharedSimulation,
} from './type-def';

const REG_CHANGE_DOMAIN_SERVICE_URL =
	process.env.REACT_APP_REG_CHANGE_DOMAIN_SERVICE_URL;
const OCP_APIM_KEY = process.env.REACT_APP_OCP_APIM_AI_SIMULATION_KEY as string;
const genAiServiceUrl = process.env.REACT_APP_GEN_AI_SERVICE_URL;
const riskLinkUrl = process.env.REACT_APP_SIMULATION_SERVICE_URL;

export const generateAISimulationPromptResult = async (
	promptKey: string,
	text: string,
	isregulator = false,
) => {
	const url = `${genAiServiceUrl}/prompts/${promptKey}/execute`;
	const res = await apiWrapper.post<APIResponse, IAPIErrorContent>(
		url,
		{ parameters: { [isregulator ? 'regulatory' : 'text']: text } },
		CONTENT_TYPE.JSON,
		getHeader(),
	);
	return res;
};

export const generateControlTestPromptResult = async (
	promptKey: string,
	text: string,
	location = 'Simulation arena',
) => {
	const url = `${genAiServiceUrl}/prompts/${promptKey}/execute`;
	const res = await apiWrapper.post<APIResponse, IAPIErrorContent>(
		url,
		{ parameters: { control_statement: text }, location },
		CONTENT_TYPE.JSON,
		getHeader(),
	);
	return res;
};

export const generateAISimulationResult = async (
	aiModelKey: string,
	text: string,
	location = 'Simulation arena',
	isregulator = false,
) => {
	const url = `${genAiServiceUrl}/prompts/models/${aiModelKey}/execute`;
	const res = await apiWrapper.post<APIResponse, IAPIErrorContent>(
		url,
		{
			parameters: { [isregulator ? 'regulatory' : 'text']: text },
			location,
		},
		CONTENT_TYPE.JSON,
		getHeader(),
	);
	return res;
};

export const genRegulationSummary = async (
	aiModelKey: string,
	text: string,
	institution: string,
	location = 'Simulation arena',
) => {
	const url = `${genAiServiceUrl}/prompts/models/${aiModelKey}/execute`;
	const res = await apiWrapper.post<APIResponse, IAPIErrorContent>(
		url,
		{
			institution,
			text,
			location,
		},
		CONTENT_TYPE.JSON,
		getHeader(),
	);
	return res;
};

export const genPromptRegulationSummary = async (
	promptKey: string,
	text: string,
	institution: string,
) => {
	const url = `${genAiServiceUrl}/prompts/${promptKey}/execute`;
	const res = await apiWrapper.post<APIResponse, IAPIErrorContent>(
		url,
		{
			institution,
			text,
		},
		CONTENT_TYPE.JSON,
		getHeader(),
	);
	return res;
};

export const genPromptControlStatement = async (
	text: string,
	controlFrequency: string,
	controlType: string,
	controlOwner: string,
	promptKey: string,
	location = 'Simulation arena',
) => {
	const url = `${genAiServiceUrl}/testing-assistant/${promptKey}/control-statement`;
	const res = await apiWrapper.post<APIResponse, IAPIErrorContent>(
		url,
		{
			parameters: {
				text,
				control_frequency: controlFrequency,
				control_type: controlType,
				control_owner: controlOwner,
			},
			location,
		},
		CONTENT_TYPE.JSON,
		getHeader(),
	);
	return res;
};

export const genControlStatement = async (
	text: string,
	controlFrequency: string,
	controlType: string,
	controlOwner: string,
	location = 'Simulation arena',
) => {
	const url = `${genAiServiceUrl}/testing-assistant/control-statement`;
	const res = await apiWrapper.post<APIResponse, IAPIErrorContent>(
		url,
		{
			parameters: {
				text,
				control_frequency: controlFrequency,
				control_type: controlType,
				control_owner: controlOwner,
			},
			location,
		},
		CONTENT_TYPE.JSON,
		getHeader(),
	);
	return res;
};

export const genControlTestScript = async (
	key: string,
	text: string,
	location = 'Simulation arena',
) => {
	const url = `${genAiServiceUrl}/testing-assistant/${key}`;
	const res = await apiWrapper.post<APIResponse, IAPIErrorContent>(
		url,
		{ parameters: { control_statement: text }, location },
		CONTENT_TYPE.JSON,
		getHeader(),
	);
	return res;
};

export const postAIModal = async (data: IAIModelDetails) => {
	const url = `${genAiServiceUrl}/simulations`;
	const res = await apiWrapper.post<IPostAIModalResp | IAPIErrorContent>(
		url,
		data,
		CONTENT_TYPE.JSON,
		getHeader(),
	);
	return res;
};

export const putAIModal = async (data: IAIModelDetails, id: string) => {
	const url = `${genAiServiceUrl}/simulations/${id}`;
	const res = await apiWrapper.put<IAIModelDetails | IAPIErrorContent>(
		url,
		data,
		CONTENT_TYPE.JSON,
		getHeader(),
	);
	return res;
};

export const saveAIRecipes = async (data: IAIRecipeDetails) => {
	const url = `${genAiServiceUrl}/recipes`;
	const res = await apiWrapper.post<IAIRecipeDetails | IAPIErrorContent>(
		url,
		data,
		CONTENT_TYPE.JSON,
		getHeader(),
	);
	return res;
};

export const getAISimulationRecipesData = async (
	queryParams: IGetAISimulationRecipes,
) => {
	const config = {
		headers: {
			'Ocp-Apim-Subscription-Key': OCP_APIM_KEY,
			'id-token': `${storageWrapper.getStorageItem('id-token')}`,
		},
	};
	const url = `${REG_CHANGE_DOMAIN_SERVICE_URL}/genai-recipe-service/recipes`;
	const resp = await apiWrapper.get<AIRecipesDataResponse, IAPIErrorContent>(
		url,
		queryParams,
		CONTENT_TYPE.JSON,
		config.headers,
	);
	return resp;
};

export const getAISimulationModelsData = async (
	queryParams: IGetAISimulationRecipes,
) => {
	const config = {
		headers: {
			'Ocp-Apim-Subscription-Key': OCP_APIM_KEY,
			'id-token': `${storageWrapper.getStorageItem('id-token')}`,
		},
	};
	const url = `${REG_CHANGE_DOMAIN_SERVICE_URL}/genai-recipe-service/simulations`;
	const resp = await apiWrapper.get<AIModelsDataResponse, IAPIErrorContent>(
		url,
		queryParams,
		CONTENT_TYPE.JSON,
		config.headers,
	);
	return resp;
};

export const shareAISimulation = async (data: IAIModelDetails) => {
	const url = `${riskLinkUrl}/simulation/share`;
	const res = await apiWrapper.post<IShareAISimulation>(url, data);
	return res;
};

export const postObligationComments = async (
	data: IObligationToPolicyComment,
) => {
	const url = `${riskLinkUrl}/simulation/comments`;
	const res = await apiWrapper.post<IObligationToPolicyComment>(url, data);
	return res;
};

export const getSharedDataByID = async (existinID: string) => {
	const url = `${riskLinkUrl}/simulation/share/${existinID}`;
	const res = await apiWrapper.get<IExistingSharedData | IAPIErrorContent>(url);
	return res;
};

export const getObligationComments = async (simulationId: string) => {
	const url = `${riskLinkUrl}/simulation/comments/${simulationId}`;
	const res = await apiWrapper.get<IObligationComments | IAPIErrorContent>(url);
	return res;
};

export const callAISimulationDeleteRecipesData = async (data: string) => {
	const config = {
		headers: {
			'Ocp-Apim-Subscription-Key': OCP_APIM_KEY,
			'id-token': `${storageWrapper.getStorageItem('id-token')}`,
		},
	};
	const url = `${REG_CHANGE_DOMAIN_SERVICE_URL}/genai-recipe-service/recipes/${data}`;
	const resp = await apiWrapper.delete<AIDeleteResponse, IAPIErrorContent>(
		url,
		null,
		{},
		CONTENT_TYPE.JSON,
		config.headers,
	);
	return resp;
};

export const callAISimulationDeleteModelsData = async (data: string) => {
	const config = {
		headers: {
			'Ocp-Apim-Subscription-Key': OCP_APIM_KEY,
			'id-token': `${storageWrapper.getStorageItem('id-token')}`,
		},
	};
	const url = `${REG_CHANGE_DOMAIN_SERVICE_URL}/genai-recipe-service/simulations/${data}`;
	const resp = await apiWrapper.delete<AIDeleteResponse, IAPIErrorContent>(
		url,
		null,
		{},
		CONTENT_TYPE.JSON,
		config.headers,
	);
	return resp;
};

export const updateSharedSimulationOnDelete = async (sharedId: string) => {
	const url = `${riskLinkUrl}/simulation/share/${sharedId}`;
	const resp = await apiWrapper.delete<AIDeleteResponse, IAPIErrorContent>(
		url,
		{},
	);
	return resp;
};

export const getAISharedSimulationData = async (
	loggedInUserId: string,
	params: IGetAISharedSimulation,
) => {
	const url = `${riskLinkUrl}/simulation/share?userId=${loggedInUserId}&pageNumber=${params?.pageNumber}&pageSize=${params?.pageSize}`;
	const res = await apiWrapper.get<AISharedDataResponse, IAPIErrorContent>(url);
	return res;
};

export const genPromptRegAnalysis = async (
	promptKey: string,
	document: string[],
	userInput: string,
) => {
	const url = `${genAiServiceUrl}/approches/${promptKey}/execute`;
	const res = await apiWrapper.post<IResultAnalysis, IAPIErrorContent>(
		url,
		{ parameters: { documents: document, user_input: userInput } },
		CONTENT_TYPE.JSON,
		getHeader(),
	);
	return res;
};

export const genRegAnalysis = async (document: string[], userInput: string) => {
	const url = `${genAiServiceUrl}/approaches/models/obligation-to-policy/execute`;
	const res = await apiWrapper.post<IResultAnalysis, IAPIErrorContent>(
		url,
		{
			parameters: { documents: document, user_input: userInput },
			location: 'Simulation arena',
		},
		CONTENT_TYPE.JSON,
		getHeader(),
	);
	return res;
};

export const getSelectedDocument = async () => {
	const url = `${genAiServiceUrl}/index/files/risk-link`;
	const resp = await apiWrapper.get<ISelectDocument, IAPIErrorContent>(
		url,
		{},
		CONTENT_TYPE.JSON,
		getHeader(),
	);
	return resp;
};

export const getDownloadFileUrl = (path: string) => {
	const url = `${genAiServiceUrl}/content/risk-link/${path}`;
	return url;
};

export const deleteObligationComments = async (simulationId: string) => {
	const url = `${riskLinkUrl}/simulation/comment/${simulationId}`;
	const resp = await apiWrapper.delete<AIDeleteResponse, IAPIErrorContent>(
		url,
		{},
	);
	return resp;
};

export const getControlUpdateDetails = async (
	data: IUpdateControlQualityCheckRequest,
) => {
	const res = await apiWrapper.post<APIResponse, IAPIErrorContent>(
		`${genAiServiceUrl}/prompts/models/language-quality-check/execute`,
		{ parameters: data, location: 'Simulation arena' },
		CONTENT_TYPE.JSON,
		getHeader(),
	);
	return res;
};

export const getControlUpdatePromptDetails = async (
	promptKey: string,
	data: IUpdateControlQualityCheckRequest,
) => {
	const res = await apiWrapper.post<APIResponse, IAPIErrorContent>(
		`${genAiServiceUrl}/prompts/${promptKey}/execute`,
		{ parameters: data, location: 'Simulation arena' },
		CONTENT_TYPE.JSON,
		getHeader(),
	);
	return res;
};

export const get5WControlPromptDetails = async (
	promptKey: string,
	data: string,
) => {
	const postData = {
		parameters: {
			content: data,
		},
	};
	const resp = await apiWrapper.post<APIResponse, IAPIErrorContent>(
		`${genAiServiceUrl}/prompts/${promptKey}/execute`,
		postData,
		CONTENT_TYPE.JSON,
		getHeader(),
	);
	return resp;
};

export const getSimilarCtrlRelationMapping = async (payload: any) => {
	const url = `${genAiServiceUrl}/relationship-mapping`;
	const res = await apiWrapper.post<any, IAPIErrorContent>(
		url,
		payload,
		CONTENT_TYPE.JSON,
		getHeader(),
	);
	return res;
};
